/**
 * ui.ts — imperative HUD side panel for the Kitchen demo.
 *
 * Mirrors reflex's SidePanel pattern: a single innerHTML template built in
 * the constructor, hot elements querySelected, panel.update(state) called
 * once per tick. State is a snapshot; callbacks are wired once.
 *
 * Blocks: Run (start/stop/reset + speed), Mode (arm + theme selectors),
 * Hyperparams (nTrees, minSamplesLeaf, coldStartK, refitEvery), Stats
 * (episode return/acc/basket, totals, memory, drift), Harness button +
 * results.
 */
import type {
  ArmMode, ThemeName,
} from './constants';
import type { BanditHyperparams } from './bandit';
import type {
  TrainEvalArmResult, FlipArmResult,
} from './harness';
import { meanEvalAcc, meanEvalF1, meanEvalDupRate, countDrifts } from './harness';

export interface HarnessState {
  status: 'idle' | 'running' | 'done' | 'error';
  progress: string;
  trainEval: TrainEvalArmResult[] | null;
  flip: FlipArmResult[] | null;
}

export interface PanelState {
  arm: ArmMode;
  theme: ThemeName;
  phase: 'idle' | 'running' | 'stopped';
  speedHz: number;
  episodeSteps: number;
  episodeReturn: number;
  episodeBasket: number;
  episodeAcc: number;
  /** Per-episode first-time F1 (positive class = should PICKUP). */
  episodeF1: number;
  /** Per-episode duplicate-pickup rate (constraint check, must stay 0). */
  episodeDupRate: number;
  /** Per-episode duplicate-encounter count (denominator for the rate). */
  episodeDupTotal: number;
  totalSteps: number;
  elapsed: number;
  dayBufferSize: number;
  longBufferSize: number;
  nObserved: number;
  nDrift: number;
  evalMode: boolean;
  headIsFit: boolean;
  /** Last decision's role: 'cold' | 'drift' | 'model'. */
  lastReason: string;
  /** Last decision's action: 0 = skip, 1 = pickup. */
  lastAction: number;
  /** True while the pretrain rollout is running (disables the checkbox). */
  pretraining: boolean;
  hp: BanditHyperparams;
  harnessState: HarnessState;
}

export interface PanelCallbacks {
  onStart(): void;
  onStop(): void;
  onReset(): void;
  onSpeed(hz: number): void;
  onArm(mode: ArmMode): void;
  onTheme(t: ThemeName): void;
  onNTrees(n: number): void;
  onMinSamplesLeaf(n: number): void;
  onColdStartK(k: number): void;
  onRefitEvery(n: number): void;
  onRunHarness(): void;
  /** Pretrain the agent on teacher rollouts before the live run starts. */
  onPretrain(): void;
}

const ARMS: ArmMode[] = ['ts', 'greedy', 'linear', 'random'];
const THEMES: ThemeName[] = ['morning', 'evening', 'all_day'];

export class SidePanel {
  readonly el: HTMLElement;
  private hot: Record<string, HTMLElement> = {};

  constructor(cb: PanelCallbacks, state: {
    arm: ArmMode; theme: ThemeName; speedHz: number;
    nTrees: number; minSamplesLeaf: number; coldStartK: number; refitEvery: number;
  }) {
    this.el = document.createElement('aside');
    this.el.className = 'panel';
    this.el.innerHTML = `
      <div class="panel-head">
        <h1>Kitchen</h1>
        <p class="tagline">Non-linear set collection · binary pickup/skip classifier</p>
      </div>

      <section class="block">
        <div class="block-title">Run</div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-start">Start</button>
          <button class="btn btn-warn" id="btn-stop">Stop</button>
          <button class="btn" id="btn-reset">Reset</button>
        </div>
        <div class="slider-row">
          <label>Speed <span id="val-speed">${state.speedHz} Hz</span></label>
          <input type="range" id="sl-speed" min="1" max="60" step="1" value="${state.speedHz}">
        </div>
        <div class="check-row">
          <button class="btn" id="btn-pretrain">Pretrain before run</button>
          <span class="check-hint" id="pretrain-status">click to bootstrap the head from teacher rollouts</span>
        </div>
      </section>

      <section class="block">
        <div class="block-title">Live state</div>
        <div class="kv"><span>role</span><span id="v-role">—</span></div>
        <div class="kv"><span>last action</span><span id="v-last">—</span></div>
        <div class="kv"><span>subset-acc</span><span id="v-ep-acc">—</span></div>
        <div class="kv"><span>F1 (first-time)</span><span id="v-ep-f1">—</span></div>
        <div class="kv"><span>dup rate</span><span id="v-ep-dup">—</span></div>
        <div class="kv"><span>return</span><span id="v-ep-return">—</span></div>
        <div class="kv"><span>steps</span><span id="v-ep-steps">—</span></div>
        <div class="kv basket-kv"><span>basket</span><span id="v-ep-basket">—</span></div>
      </section>

      <section class="block">
        <div class="block-title">Mode</div>
        <div class="seg-row" id="seg-arm">
          ${ARMS.map((a) => `<button class="seg seg-arm-${a} ${a === state.arm ? 'active' : ''}" data-arm="${a}">${a}</button>`).join('')}
        </div>
        <div class="seg-row" id="seg-theme">
          ${THEMES.map((t) => `<button class="seg ${t === state.theme ? 'active' : ''}" data-theme="${t}">${t}</button>`).join('')}
        </div>
      </section>

      <section class="block">
        <div class="block-title">Hyperparameters</div>
        <div class="slider-row"><label>n_trees <span id="val-ntrees">${state.nTrees}</span></label>
          <input type="range" id="sl-ntrees" min="10" max="80" step="5" value="${state.nTrees}"></div>
        <div class="slider-row"><label>min_samples_leaf <span id="val-leaf">${state.minSamplesLeaf}</span></label>
          <input type="range" id="sl-leaf" min="1" max="30" step="1" value="${state.minSamplesLeaf}"></div>
        <div class="slider-row"><label>cold_start_k <span id="val-cold">${state.coldStartK}</span></label>
          <input type="range" id="sl-cold" min="4" max="80" step="4" value="${state.coldStartK}"></div>
        <div class="slider-row"><label>refit_every <span id="val-refit">${state.refitEvery}</span></label>
          <input type="range" id="sl-refit" min="2" max="40" step="2" value="${state.refitEvery}"></div>
      </section>

      <section class="block">
        <div class="block-title">Memory</div>
        <div class="kv"><span>day buffer</span><span id="v-day">—</span></div>
        <div class="kv"><span>long buffer</span><span id="v-long">—</span></div>
        <div class="kv"><span>observed</span><span id="v-obs">—</span></div>
        <div class="kv"><span>drift events</span><span id="v-drift">—</span></div>
        <div class="kv"><span>head fit</span><span id="v-fit">—</span></div>
      </section>

      <section class="block">
        <div class="block-title">Evaluation harness</div>
        <button class="btn" id="btn-harness">Run train/eval (4 arms × 5 seeds)</button>
        <div class="kv"><span>status</span><span id="v-h-status">idle</span></div>
        <div class="kv"><span>progress</span><span id="v-h-progress">—</span></div>
        <div id="h-results"></div>
      </section>
    `;

    this.bind('btn-start', 'click', cb.onStart);
    this.bind('btn-stop', 'click', cb.onStop);
    this.bind('btn-reset', 'click', cb.onReset);
    this.bind('btn-harness', 'click', cb.onRunHarness);
    this.bind('btn-pretrain', 'click', cb.onPretrain);
    this.wireSlider('sl-speed', 'val-speed', (v) => `${v} Hz`, cb.onSpeed);
    this.wireSlider('sl-ntrees', 'val-ntrees', (v) => `${v}`, cb.onNTrees);
    this.wireSlider('sl-leaf', 'val-leaf', (v) => `${v}`, cb.onMinSamplesLeaf);
    this.wireSlider('sl-cold', 'val-cold', (v) => `${v}`, cb.onColdStartK);
    this.wireSlider('sl-refit', 'val-refit', (v) => `${v}`, cb.onRefitEvery);
    this.wireSegRow('#seg-arm', 'data-arm', (m) => cb.onArm(m as ArmMode));
    this.wireSegRow('#seg-theme', 'data-theme', (t) => cb.onTheme(t as ThemeName));

    const ids = ['val-speed', 'v-role', 'v-last', 'v-ep-return', 'v-ep-acc', 'v-ep-f1', 'v-ep-dup', 'v-ep-steps', 'v-ep-basket',
      'v-day', 'v-long', 'v-obs', 'v-drift', 'v-fit', 'v-h-status', 'v-h-progress',
      'h-results', 'btn-pretrain', 'pretrain-status'];
    for (const id of ids) {
      const e = this.el.querySelector(`#${id}`) as HTMLElement | null;
      if (e) this.hot[id] = e;
    }
  }

  private bind(id: string, evt: string, fn: () => void): void {
    const e = this.el.querySelector(`#${id}`) as HTMLElement | null;
    if (e) e.addEventListener(evt, fn);
  }

  private wireSlider(id: string, valId: string, fmt: (v: number) => string,
                     onChange: (v: number) => void): void {
    const sl = this.el.querySelector(`#${id}`) as HTMLInputElement | null;
    const val = this.el.querySelector(`#${valId}`) as HTMLElement | null;
    if (!sl || !val) return;
    sl.addEventListener('input', () => {
      const v = Number(sl.value);
      val.textContent = fmt(v);
      onChange(v);
    });
  }

  private wireSegRow(sel: string, attr: string, onChange: (v: string) => void): void {
    const row = this.el.querySelector(sel) as HTMLElement | null;
    if (!row) return;
    row.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target || !target.hasAttribute(attr)) return;
      const val = target.getAttribute(attr)!;
      for (const child of Array.from(row.children)) {
        child.classList.toggle('active', child === target);
      }
      onChange(val);
    });
  }

  update(s: PanelState): void {
    const set = (id: string, v: string): void => {
      const e = this.hot[id];
      if (e) e.textContent = v;
    };
    // Live state block.
    set('v-role', s.lastReason || '—');
    set('v-last', s.lastAction === 1 ? 'pickup' : (s.lastAction === 0 ? 'skip' : '—'));
    set('v-ep-acc', s.episodeAcc.toFixed(3));
    set('v-ep-f1', s.episodeF1.toFixed(3));
    // dup-rate should be 0.000; show the count so the viewer can see this is
    // a constraint check (e.g. "0.000 (3 encountered)" means the agent
    // skipped all 3 duplicates it saw this episode).
    set('v-ep-dup', `${s.episodeDupRate.toFixed(3)} (${s.episodeDupTotal} seen)`);
    set('v-ep-return', s.episodeReturn.toFixed(2));
    set('v-ep-steps', `${s.episodeSteps}`);
    const basketEl = this.hot['v-ep-basket'];
    if (basketEl) {
      const ITEMS = ['☕', '🍺', '🥚', '🌾', '🍯', '🍞', '🥛', '🧃'];
      const items: string[] = [];
      for (let i = 0; i < 8; i++) if (s.episodeBasket & (1 << i)) items.push(ITEMS[i]);
      basketEl.textContent = items.join(' ') || '(empty)';
    }
    // Memory block.
    set('v-day', `${s.dayBufferSize}`);
    set('v-long', `${s.longBufferSize}`);
    set('v-obs', `${s.nObserved}`);
    set('v-drift', `${s.nDrift}`);
    set('v-fit', s.headIsFit ? 'yes' : 'no');
    // Pretrain button state.
    const ptBtn = this.hot['btn-pretrain'] as HTMLButtonElement | undefined;
    const ptStatus = this.hot['pretrain-status'];
    if (ptBtn && ptStatus) {
      if (s.pretraining) {
        ptBtn.disabled = true;
        ptBtn.textContent = 'Pretraining…';
        ptStatus.textContent = 'rolling out teacher episodes';
      } else if (s.headIsFit) {
        ptBtn.disabled = true;
        ptBtn.textContent = 'Pretrained ✓';
        ptStatus.textContent = `head fit on ${s.longBufferSize} transitions — ready to run`;
      } else {
        ptBtn.disabled = false;
        ptBtn.textContent = 'Pretrain before run';
        ptStatus.textContent = 'click to bootstrap the head from teacher rollouts';
      }
    }
    // Harness block.
    set('v-h-status', s.harnessState.status);
    set('v-h-progress', s.harnessState.progress);
    const results = this.hot['h-results'];
    if (results && s.harnessState.status === 'done' && s.harnessState.trainEval) {
      const te = s.harnessState.trainEval;
      const fl = s.harnessState.flip;
      // eval acc / F1 / dup-rate / flip drifts. dup-rate must be 0.000 —
      // it's a constraint regression check, not a quality metric.
      const lines: string[] = ['<table class="h-table"><tr><th>arm</th><th>acc</th><th>F1</th><th>dup</th><th>drifts</th></tr>'];
      for (const r of te) {
        const acc = meanEvalAcc(r);
        const f1 = meanEvalF1(r);
        const dup = meanEvalDupRate(r);
        const flipR = fl?.find((f) => f.arm === r.arm);
        const drifts = flipR ? countDrifts(flipR) : 0;
        const dupFlag = dup > 0.001 ? '⚠' : '';
        lines.push(`<tr><td>${r.arm}</td><td>${acc.toFixed(3)}</td><td>${f1.toFixed(3)}</td><td>${dup.toFixed(3)}${dupFlag}</td><td>${drifts}</td></tr>`);
      }
      lines.push('</table>');
      results.innerHTML = lines.join('');
    }
  }
}
